/**
 * The in-mission score: one sixteen-bar song whose tracks are LAYERS (07 §4). At alert 0 only a
 * drone and, now and then, a distant fragment of the theme; a ticking pulse when someone is
 * suspicious; a bass ostinato when they are searching; drums and a lead when the alarm is up.
 * The last minute adds a clock; the Osprey brings the theme back - in the major if the job is done.
 *
 * Written in D minor. Each biome re-keys and re-voices it: arctic is higher, slower and glassy;
 * desert is phrygian with dry percussion; temperate is lower and warmer, with toms.
 */

import type { Biome } from '../../sim/types';
import { instrument, type InstrumentId } from './instruments';
import { type Song, track, type Track, transposed } from './pattern';
import { toMajor, toPhrygian } from './theory';
import { TITLE_MELODY } from './title';

const TONIC_D = 2;
const REST = '. . . . . . . . . . . . . . . .';
const HOLD = '- - - - - - - - - - - - - - - -';

/** A four-bar phrase four times over: the song is sixteen bars so the full theme fits the pickup. */
const x4 = (bars: readonly string[]): string[] => [...bars, ...bars, ...bars, ...bars];
const held = (note: string): string[] => x4([`${note} - - - - - - - - - - - - - - -`, HOLD, HOLD, HOLD]);

const HAT = x4([
  'x . o . x . o . x . o . x . o x',
  'x . o . x . o . x . o . x . o .',
  'x . o . x . o . x . o . x . o x',
  'x . o . x . o x x . o . x o x o',
]);

const OSTINATO = x4([
  'D2 . . D2 . . D2 . . D2 . . F2 . E2 .',
  'D2 . . D2 . . D2 . . D2 . . A1 . C2 .',
  'Bb1 . . Bb1 . . Bb1 . . Bb1 . . D2 . C2 .',
  'C2 . . C2 . . C2 . A1 . . A1 . . C#2 .',
]);

const KICK = x4([
  'X . . . . . X . . . X . . . . .',
  'X . . . . . X . . . X . . . x .',
  'X . . . . . X . . . X . . . . .',
  'X . . . . . X . X . X . . . x .',
]);
const SNARE = x4([
  '. . . . X . . o . o . . X . . o',
  '. . . . X . . o . o . . X . o o',
  '. . . . X . . o . o . . X . . o',
  '. . . . X . . o . o . . X x x x',
]);
const TOMS = x4([REST, REST, REST, '. . . . . . . . . . . . G3 G3 E3 C3']);

const RIFF = x4([
  'D5 . D5 . C5 . D5 . . . A4 . C5 . A4 .',
  'D5 . D5 . C5 . D5 . F5 - - - E5 - C5 -',
  'Bb4 . Bb4 . A4 . Bb4 . . . F4 . A4 . F4 .',
  'A4 . A4 . G4 . A4 . C#5 - - - E5 - - -',
]);
const SHIMMER = x4([
  'D4 F4 A4 F4 D4 F4 A4 F4 D4 F4 A4 F4 D4 F4 A4 F4',
  'D4 F4 A4 F4 D4 F4 A4 F4 D4 F4 A4 F4 D4 F4 A4 F4',
  'D4 F4 Bb4 F4 D4 F4 Bb4 F4 D4 F4 Bb4 F4 D4 F4 Bb4 F4',
  'C4 E4 G4 E4 C4 E4 G4 E4 C#4 E4 A4 E4 C#4 E4 A4 E4',
]);

/** The clock: a tick-tock on the dominant that doubles up as each phrase runs out. */
const CLOCK = x4([
  'A5 . . . E5 . . . A5 . . . E5 . . .',
  'A5 . . . E5 . . . A5 . . . E5 . A5 .',
  'A5 . E5 . A5 . E5 . A5 . E5 . A5 . E5 .',
  'A5 . E5 . A5 . E5 . A5 E5 A5 E5 A5 E5 A5 E5',
]);

/** Twice in sixteen bars, a soft fragment of the title's opening call, as if from over the hill. */
const MOTIF = [
  REST, REST, REST, REST, REST, REST,
  'D4_ - - D4_ D4_ - F4_ - A4_ - - - - - - -', REST,
  REST, REST, REST, REST, REST, REST,
  'A4_ - - A4_ A4_ - G4_ - F4_ - - - D4_ - - -', REST,
];

/** The flyover's snare roll, building bar by bar toward the jump buzzer. */
const ROLL = x4([
  'o . o . o . o . o . o . o . o o',
  'o o o . o o o . o o o . o o o o',
  'x o o o x o o o x o o o x o x o',
  'x o x o x o x o x x x x X x X X',
]);

/** D pedal under the major-key reprise: no third, so it sits under either mode. */
const PEDAL = TITLE_MELODY.map(() => 'D2 . D2 . D2 . D3 . D2 . D2 . A2 . D3 .');

interface BiomeScore {
  bpm: number;
  transpose: number;
  phrygian: boolean;
  swap: Partial<Record<InstrumentId, InstrumentId>>;
  toms: boolean;
}

export const BIOME_SCORE: Record<Biome, BiomeScore> = {
  arctic: { bpm: 96, transpose: 2, phrygian: false, swap: { arp: 'glass' }, toms: false },
  desert: { bpm: 104, transpose: 0, phrygian: true, swap: { hat: 'rim' }, toms: false },
  temperate: { bpm: 100, transpose: -2, phrygian: false, swap: { bass: 'warmBass' }, toms: true },
};

function baseTracks(): Track[] {
  const major = transposed(track('lift', 'lead', 'lift', 0.85, TITLE_MELODY), 0, (m) => toMajor(m, TONIC_D));
  return [
    track('drone', 'pad', 'drone', 1, held('D2')),
    track('drone5', 'pad', 'drone', 0.7, held('A2')),
    track('motif', 'bugle', 'motif', 0.45, MOTIF),
    track('hat', 'hat', 'hat', 0.8, HAT),
    track('ostinato', 'bass', 'bass', 0.85, OSTINATO),
    track('kick', 'kick', 'drums', 0.8, KICK),
    track('snare', 'snare', 'drums', 0.7, SNARE),
    track('toms', 'tom', 'drums', 0.7, TOMS),
    track('riff', 'lead', 'lead', 0.75, RIFF),
    track('shimmer', 'arp', 'lead', 0.8, SHIMMER),
    track('clock', 'ping', 'urgency', 0.9, CLOCK),
    track('reprise', 'lead', 'extraction', 0.85, TITLE_MELODY),
    major,
    track('pedal', 'bass', 'lift', 0.7, PEDAL),
    track('roll', 'snare', 'roll', 0.6, ROLL),
  ];
}

/** The mission song in a biome's key, mode, tempo and voicing. */
export function missionSong(biome: Biome): Song {
  const score = BIOME_SCORE[biome];
  const tracks = baseTracks()
    .filter((t) => score.toms || t.name !== 'toms')
    .map((t) => {
      const swapped: Track = { ...t, instrument: score.swap[t.instrument] ?? t.instrument };
      if (instrument(swapped.instrument).kind === 'drum') return swapped;
      // Phrygian colour is for the mission's own layers. The theme comes back as the player knows it.
      const own = t.layer !== 'lift' && t.layer !== 'extraction';
      const alter = score.phrygian && own ? (m: number): number => toPhrygian(m, TONIC_D) : undefined;
      return transposed(swapped, score.transpose, alter);
    });
  return { name: `mission-${biome}`, bpm: score.bpm, steps: TITLE_MELODY.length * 16, tracks };
}
